// Course OCR — POSTs a scorecard image to the backend extract-course
// endpoint and parses the structured response into the shapes the
// CourseAddModal verify form expects.

import { Hole, Tee } from "./types";

interface ApiTee {
  name: string;
  slope?: number;
  rating?: number;
  yardages?: number[];
}

interface ApiHole {
  number: number;
  par: number;
  handicap: number;
  yardage?: number;
}

interface ApiResponse {
  name?: string;
  holes?: ApiHole[];
  tees?: ApiTee[];
  error?: string;
}

export interface ExtractedCourse {
  name: string;
  holes: Hole[];
  tees: Tee[];
}

const TEE_PALETTE = [
  "#1d4ed8",
  "#d4a017",
  "#c0c0c0",
  "#b87333",
  "#16a34a",
  "#d63b3b",
  "#0047ab",
  "#797679",
];

// Best-effort colour for a tee by its name. Falls back to a palette
// position so distinct tees still render distinct dots on the
// scorecard. Mirrors the small lookup we use elsewhere for live data.
const NAME_COLORS: Record<string, string> = {
  black: "#111111",
  blue: "#1d4ed8",
  cobalt: "#0047ab",
  white: "#f3f4f6",
  yellow: "#f5d142",
  red: "#d63b3b",
  green: "#16a34a",
  gold: "#d4a017",
  silver: "#c0c0c0",
  copper: "#b87333",
  pewter: "#797679",
};

function colorFor(name: string, idx: number): string {
  const first = name.toLowerCase().split(/[^a-z]+/).filter(Boolean)[0];
  return (first && NAME_COLORS[first]) || TEE_PALETTE[idx % TEE_PALETTE.length];
}

function teeIdFromName(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return slug || "tee";
}

export async function extractCourseFromImage(
  file: File,
): Promise<ExtractedCourse> {
  const form = new FormData();
  form.append("file", file);

  const res = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/caddiegames/extract-course`,
    {
      method: "POST",
      credentials: "include",
      body: form,
    },
  );

  if (!res.ok) {
    let message = `Extraction failed (${res.status})`;
    try {
      const data: ApiResponse = await res.json();
      if (data.error) message = data.error;
    } catch {
      // ignore — keep the default
    }
    throw new Error(message);
  }

  const data: ApiResponse = await res.json();

  // Backend already pads to 18 holes; this is just the type bridge from
  // the API "handicap" field to our internal "si" naming.
  const holes: Hole[] = (data.holes || []).map((h) => ({
    number: h.number,
    par: h.par,
    si: h.handicap,
    yardage: typeof h.yardage === "number" ? h.yardage : undefined,
  }));

  const tees: Tee[] = (data.tees || []).map((t, idx) => ({
    id: teeIdFromName(t.name),
    name: t.name,
    color: colorFor(t.name, idx),
    slope: t.slope,
    rating: t.rating,
    yardages: t.yardages,
  }));

  return {
    name: data.name || "",
    holes,
    tees,
  };
}
